import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ShellSidebar from "./ShellSidebar";
import ShellMessagesBar from "./ShellMessagesBar";
import { MobileTopBar, TabBar, TopActions } from "./ShellTopBar";
import { useBalance, useInbox, useIsDesktop, useMySpaces } from "./useShellData";

/*
  The frame every signed-in-or-not page sits in (handoff AppShell).
  ≥ 1024px: Sidebar (280, or the 80px rail beside full-height windows) +
  floating Top actions + Messages bar; content starts 24px after the sidebar,
  104px down (Figma B1).
  < 1024px: Top bar + Tab bar, which holds chats; Menu opens the Sidebar as a drawer.
*/
const AppShell = ({ isDark, toggleTheme, setToast, isWindow = false, rail = false, flush = false, children }) => {
  const desktop = useIsDesktop();
  const { pathname } = useLocation();
  const { owned, joined } = useMySpaces();
  const balance = useBalance();
  const inbox = useInbox(setToast);
  const [drawer, setDrawer] = useState(false);
  const closeDrawer = useCallback(() => setDrawer(false), []);
  // The inbox page has its own list; the bar would only cover its composer.
  const showMessagesBar = !pathname.startsWith("/messages");

  useEffect(() => closeDrawer(), [pathname, closeDrawer]);

  useEffect(() => {
    if (!drawer) return undefined;
    const onKey = (event) => event.key === "Escape" && closeDrawer();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer, closeDrawer]);

  const sidebarProps = { owned, joined, isDark, toggleTheme, setToast };
  const theme = isDark ? "dark" : "light";

  if (desktop) {
    return (
      <div data-theme={theme} className="relative min-h-screen bg-bg-base text-label-primary">
        <div className="fixed inset-y-0 left-0 z-30">
          <ShellSidebar {...sidebarProps} collapsed={isWindow || rail} />
        </div>
        <main className={`relative min-h-screen ${isWindow || rail ? "pl-[80px]" : "pl-[280px]"}`}>
          <TopActions balance={balance} isDark={isDark} />
          {/* A screen can put its title here, in the row of the top actions. */}
          <div id="shell-title" className="absolute left-[304px] top-space-24 flex h-[56px] items-center" />
          <div
            className={
              isWindow
                ? `flex h-screen flex-col ${flush ? "" : "px-space-12 pb-space-12 pt-[88px]"}`
                : `flex flex-col gap-space-24 px-space-24 pb-[120px] ${rail ? "pt-[88px]" : "pt-[104px]"}`
            }
          >
            {children}
          </div>
        </main>
        {showMessagesBar && <ShellMessagesBar inbox={inbox} />}
      </div>
    );
  }

  return (
    <div data-theme={theme} className="relative min-h-screen bg-bg-base text-label-primary">
      <MobileTopBar balance={balance} isDark={isDark} />
      <main
        className={
          isWindow
            ? "flex h-[100dvh] flex-col px-space-8 pb-[80px] pt-[68px]"
            : "flex flex-col gap-space-24 px-space-16 pb-[96px] pt-[84px] min-[600px]:px-space-24"
        }
      >
        {children}
      </main>
      <TabBar onMenu={() => setDrawer(true)} unread={inbox.unread} isDark={isDark} />
      {drawer && (
        <>
          <div className="fixed inset-0 z-[49] bg-overlay-scrim animate-ds-fade-in" onClick={closeDrawer} aria-hidden="true" />
          <div role="dialog" aria-modal="true" className="fixed inset-y-0 left-0 z-50 shadow-overlay animate-ds-drawer-in">
            <ShellSidebar {...sidebarProps} onNavigated={closeDrawer} />
          </div>
        </>
      )}
    </div>
  );
};

export default AppShell;
